import { useQuery, useQueryClient, useMutation } from '@tanstack/react-query';
import { db } from '@/lib/db';
import { realtime } from '@/lib/realtime';
import { useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';

export interface Notification {
  id: string;
  tipo: string;
  titulo: string;
  mensagem: string;
  link: string | null;
  lida: boolean;
  created_at: string;
  metadata: any;
}

export function useNotifications() {
  const queryClient = useQueryClient();
  const { userRole } = useAuth();
  
  // Buscar notificações
  const { data: notifications = [], isLoading } = useQuery({
    queryKey: ['notifications'],
    queryFn: async () => {
      const { data, error } = await db
        .from('notifications')
        .select('*')
        .order('created_at', { ascending: false })
        .limit(50);
      
      if (error) throw error;
      return data as Notification[];
    },
    enabled: !!userRole,
    staleTime: 30000, // 30s - Realtime atualiza lista automaticamente
    refetchOnWindowFocus: false,
  });
  
  // Subscription real-time APENAS para atualizar lista
  // NÃO dispara som/toast (isso já é feito pela subscription existente)
  useEffect(() => {
    if (!userRole) return;

    // Walter 2026-05-18 — revisão realtime #0 (parte 2): removido guard
    // `isSystemActive()`. Mesmo motivo dos hooks de realtime (ver
    // useProductionCoreSync). Sem isso, notificações novas não chegavam
    // a usuários ativos no domingo / fora 5-20h BRT.

    const channel = realtime
      .channel('notifications-realtime')
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'notifications'
        },
        (payload) => {
          // Apenas adicionar à lista (SEM som/toast para evitar duplicação)
          queryClient.setQueryData<Notification[]>(['notifications'], (old) => 
            [payload.new as Notification, ...(old || [])]
          );
        }
      )
      .subscribe();
    
    return () => {
      realtime.removeChannel(channel);
    };
  }, [userRole, queryClient]);
  
  // Marcar como lida
  const markAsRead = useMutation({
    mutationFn: async (notificationId: string) => {
      const { error } = await db
        .from('notifications')
        .update({ lida: true })
        .eq('id', notificationId);
      
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['notifications'] });
    },
  });
  
  // Marcar todas como lidas
  const markAllAsRead = useMutation({
    mutationFn: async () => {
      const { error } = await db
        .from('notifications')
        .update({ lida: true })
        .eq('lida', false);
      
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['notifications'] });
    },
  });
  
  const unreadCount = notifications.filter(n => !n.lida).length;
  
  return {
    notifications,
    isLoading,
    unreadCount,
    markAsRead: markAsRead.mutate,
    markAllAsRead: markAllAsRead.mutate,
  };
}
